// Product brief for ManhattanLife OmniFlex™ Short-Term Care. See cmTwo.ts for
// the format intent (mechanics a reviewer can't see from numbers alone + things
// to eyeball). Q-ids reference the open carrier questions in
// docs/plans/manhattanLifeStc-strategy-plan.md §8.

export const manhattanLifeStcBrief = `**ManhattanLife OmniFlex™ Short-Term Care** — standalone, day-denominated
short-term facility care. There is **no dollar pool, no death benefit, and no
EOB pool**: the base pool is the unlimited sentinel (never accrues; renders as
"Unlimited") and the benefit-period / lifetime DAY counters are the only
binding constraint. Pure per-day **indemnity** (Q-INDEM): pays the accrued
daily benefit regardless of charges. Single-life only.

Accrued Daily Benefit — the growth model (NOT a generic inflation rate):
- \`accrued(y) = elected × min(0.5 + 0.1×min(y−1, 5) + 0.05×max(0, min(y−1, 25)−5), 2.0)\`
  where \`y\` is the POLICY YEAR (\`y − 1\` anniversaries passed; the carrier's
  "N years after purchase" = policy year N + 1).
- Q-RAMP (resolved): the 50% → 100% ramp is a BASE-POLICY feature and applies
  unconditionally. The Simple Inflation rider (\`policyInflationProtection >
  0\`) gates ONLY the +5%-of-elected steps beyond anniversary 5; without it the
  factor matures at 1.0 on the 5th anniversary and stays flat.
- Cap: 2× elected, reached at anniversary 25 and frozen after. Kentucky's 3%
  compound variant is deferred (Q-KY).
- Validated against the brochure's Anthony example: $280 elected, claim after
  10 anniversaries → $350/day accrued, $175/day Fast-50, $31,500 over 180 days.

PER-TRACK day counters (in \`strategyState\`): the Home Health Care rider is
"almost a separate policy" — its own daily maximum, benefit period and
elimination period — so facility and home care drain INDEPENDENT counters
and cannot borrow from each other:
- FACILITY track — \`facilityBenefitPeriodDaysRemaining\` (90/180/270/360
  carrier days = \`policyBenefitPeriodMonths × 30\`) and
  \`facilityLifetimeDaysRemaining\` (2× the facility period; NEVER restores).
- HOME track — \`homeBenefitPeriodDaysRemaining\` /
  \`homeLifetimeDaysRemaining\`, seeded from \`homeCareBenefitPeriodMonths ×
  30\` (falls back to the facility months on saved policies predating the
  field; both 0 without the rider).
- A row drains ONLY its funding track: home iff the row is home care AND the
  rider's benefit pays it; everything else — including Fast-50's
  riderless-home facility fallback — charges facility. A track's period and
  lifetime decrement together 1:1 on paid days (Fast-50 too, Q-F50-DRAW);
  payable days = min(eligible days, track period, track lifetime).
- **Restoration**: after 180 CONSECUTIVE care-free AND benefit-free days
  (Q-REST strict — ONE policy-wide streak; any paid benefit or care-need
  month resets \`benefitFreeDayStreak\`), EACH track below its original size
  restores to min(original track BP, track lifetime remaining). Repeatable to
  that track's lifetime max.
  \`strategyDeltas.facilityRestorationDaysRestored\` /
  \`homeRestorationDaysRestored\` report the restored days; the row carries a
  "…benefit period restored" milestone (track-qualified when the rider is
  elected).
- TERMINAL exhaustion = EVERY elected track's lifetime at 0 ("Lifetime
  maximum benefit period exhausted" milestone; \`ltcPoolExhausted\` keys off
  the summed lifetime counters). One spent track ("Facility/Home care
  lifetime maximum exhausted") and a depleted-but-restorable period
  ("…benefit period depleted") are NOT terminal.

Fast-50™ (explicit election, \`isFast50Election\` — never auto-optimized):
- Pays 0.5 × accrued daily benefit as cash for care-need days in ANY
  environment — including home WITHOUT the HHC rider (Fast-50 attaches to the
  facility benefit, so riderless home months use the FACILITY accrued benefit
  and drain the FACILITY track).
- The elimination period is WAIVED for payment but Fast-50 days do NOT satisfy
  it: the WP state carries forward UNCONSUMED all claim long.
- No duration cap (Q-F50-DUR); days consume the funding track's counters 1:1.

Per-environment benefits (standard indemnity election):
- Facility (NH/AL): the elected facility daily benefit × accrual factor.
- Home: \`dailyHomeBenefitAmount\` × factor — populated iff the HHC rider was
  elected. An EMPTY home field means $0 home benefit (no sparse-field
  fallback) and those months consume NO counter days (not a claim).
- Independent Living: never covered.

Things to eyeball:
- \`base_pool\` is the sentinel: never accrues, never depletes (flat at the
  magic number on every row); \`eob\` and \`db\` are $0 on every row.
- Cumulative \`facilityBenefitPeriodDaysConsumed\` (resp. home) can reach at
  most 2× that track's benefit period (one full restoration cycle), never
  more — and a home row must never move the facility counters (or vice
  versa), except the riderless-home Fast-50 fallback which charges facility.
- Under Fast-50 the WP balances must be IDENTICAL on every row (unconsumed).
- Ramp sanity: a year-3 claim on $200 elected pays $140/day with OR without
  the rider (the ramp is base-policy); rider vs. no-rider only diverges past
  the 5th anniversary.
`;
